import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, MapPin, Calendar, CircleDollarSign, Check } from "lucide-react";
import { Page, PageHero, Eyebrow } from "@/components/PageShell";
import { PROJECTS, IMAGES } from "@/data/site";

const FILTERS = [
  { key: "all", label: "All Regions", testid: "region-filter-all" },
  { key: "latam", label: "Latin America", testid: "region-filter-latam" },
  { key: "africa", label: "Africa", testid: "region-filter-africa" },
  { key: "swiss", label: "Switzerland", testid: "region-filter-swiss" },
];

export default function Projects() {
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(null);

  const visible =
    filter === "all" ? PROJECTS : PROJECTS.filter((p) => p.regionKey === filter);

  return (
    <Page testid="projects-page">
      <PageHero
        eyebrow="Flagship Projects"
        title="Four campaigns. Three continents. One standard."
        subtitle="A selection of landmark marketing mandates delivered for Mirabaud investment projects across Latin America, Africa, and Switzerland."
        image={IMAGES.jetDeau}
        testid="projects-hero"
      />

      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-24" data-testid="global-project-map">
        <div className="flex flex-wrap gap-3 mb-14" data-testid="region-filters">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              data-testid={f.testid}
              className={`px-5 py-2.5 text-xs font-mono-label tracking-[0.15em] uppercase border transition-all duration-300 ${
                filter === f.key
                  ? "border-[#C5A059] bg-[rgba(197,160,89,0.12)] text-[#E6C687]"
                  : "border-[rgba(197,160,89,0.2)] text-[#A0AAB2] hover:border-[rgba(197,160,89,0.45)] hover:text-[#F8F9FA]"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        <motion.div layout className="grid md:grid-cols-2 gap-10">
          <AnimatePresence mode="popLayout">
            {visible.map((p) => (
              <motion.article
                layout
                key={p.id}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.96 }}
                transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                className="group border border-[rgba(197,160,89,0.2)] bg-[#162248] card-hover cursor-pointer"
                onClick={() => setSelected(p)}
                data-testid={p.testid}
              >
                <div className="overflow-hidden h-64 relative">
                  <img src={p.image} alt={p.title} className="w-full h-full object-cover img-fade" />
                  <span className="absolute top-4 left-4 bg-[rgba(11,19,43,0.85)] backdrop-blur px-3 py-1.5 font-mono-label text-[10px] tracking-[0.2em] uppercase text-[#E6C687] border border-[rgba(197,160,89,0.3)]">
                    {p.region}
                  </span>
                </div>
                <div className="p-8">
                  <div className="flex items-center justify-between gap-4">
                    <span className="font-mono-label text-[10px] tracking-[0.2em] uppercase text-[#6C7A89]">
                      {p.country}
                    </span>
                    <span className="font-mono-label text-[10px] tracking-[0.2em] text-[#6C7A89]">
                      {p.year}
                    </span>
                  </div>
                  <h3 className="mt-4 font-serif-display text-2xl text-[#F8F9FA] leading-snug">
                    {p.title}
                  </h3>
                  <p className="mt-4 text-sm leading-relaxed text-[#A0AAB2]">{p.description}</p>
                  <div className="mt-6 pt-5 border-t border-[rgba(197,160,89,0.15)] flex items-center justify-between">
                    <span className="font-serif-display text-xl text-[#E6C687]">{p.capital}</span>
                    <span className="text-xs text-[#C5A059] tracking-wide opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      View case detail →
                    </span>
                  </div>
                </div>
              </motion.article>
            ))}
          </AnimatePresence>
        </motion.div>
      </section>

      <AnimatePresence>
        {selected && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] flex items-center justify-center p-4 sm:p-8 bg-[rgba(11,19,43,0.9)] backdrop-blur-md"
            onClick={() => setSelected(null)}
            data-testid="project-modal-backdrop"
          >
            <motion.div
              initial={{ opacity: 0, y: 40, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 24, scale: 0.97 }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              className="relative w-full max-w-3xl max-h-[88vh] overflow-y-auto border border-[rgba(197,160,89,0.35)] bg-[#101B3B]"
              onClick={(e) => e.stopPropagation()}
              data-testid="project-modal"
            >
              <button
                onClick={() => setSelected(null)}
                className="absolute top-4 right-4 z-10 w-9 h-9 flex items-center justify-center bg-[rgba(11,19,43,0.85)] border border-[rgba(197,160,89,0.3)] text-[#E6C687] hover:bg-[#C5A059] hover:text-[#0B132B] transition-colors"
                data-testid="project-modal-close"
                aria-label="Close"
              >
                <X size={17} />
              </button>
              <div className="h-64 sm:h-80 overflow-hidden">
                <img src={selected.image} alt={selected.title} className="w-full h-full object-cover" />
              </div>
              <div className="p-8 sm:p-10">
                <Eyebrow>
                  {selected.region} · {selected.country}
                </Eyebrow>
                <h3 className="mt-4 font-serif-display text-2xl sm:text-3xl text-[#F8F9FA] leading-snug">
                  {selected.title}
                </h3>
                <p className="mt-5 text-base leading-relaxed text-[#A0AAB2]">{selected.description}</p>

                <div className="mt-8 grid sm:grid-cols-3 gap-5">
                  {[
                    { icon: CircleDollarSign, label: "Capital Volume", value: selected.capital },
                    { icon: Calendar, label: "Engagement", value: selected.year },
                    { icon: MapPin, label: "Markets", value: selected.country },
                  ].map(({ icon: Icon, label, value }) => (
                    <div key={label} className="border border-[rgba(197,160,89,0.2)] bg-[#162248] p-5">
                      <Icon size={17} className="text-[#C5A059]" />
                      <p className="mt-3 font-mono-label text-[9px] tracking-[0.2em] uppercase text-[#6C7A89]">
                        {label}
                      </p>
                      <p className="mt-1.5 text-sm font-semibold text-[#F8F9FA]">{value}</p>
                    </div>
                  ))}
                </div>

                <div className="mt-9">
                  <span className="font-mono-label text-[10px] tracking-[0.2em] uppercase text-[#C5A059]">
                    Key Deliverables
                  </span>
                  <ul className="mt-4 grid sm:grid-cols-2 gap-3">
                    {selected.deliverables.map((d) => (
                      <li key={d} className="flex items-start gap-3 text-sm text-[#A0AAB2]">
                        <Check size={15} className="text-[#C5A059] mt-0.5 shrink-0" />
                        {d}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </Page>
  );
}
